import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ColumnFilled, ColumnFree, DefaultFilled, DefaultFree, DefaultText, MediaFilled, PriceDownText, PriceUpText, SquareMiddlePrice } from "../preview-widget/components";
import ListText from "../preview-widget/components/list-text/ListText";
import Price from "../Price";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { openItem } from "@/store/uiSlice";
import { getOrderItemPrice } from "@/utils/order";
import { sanitizeNumber } from "@/utils/numbers";
import { Content, OrderItem } from "@/types";
import { useNavigate } from "react-router-dom";
import { getThumbnailImage } from "@/utils/media";

interface GridViewProps<I extends Content> {
  items: I[];
  previewWidget: string;
}

const GridView = <I extends Content>({ items = [], previewWidget = "default-free" }: GridViewProps<I>) => {
  const activeOrganization = useAppSelector((store) => store.ui.activeOrganization);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const PreviewWidget = useMemo(() => {
    switch (previewWidget) {
      case "default-text":
        return DefaultText;
      case "list-text":
        return ListText;
      case "price-down-text":
        return PriceDownText;
      case "price-up-text":
        return PriceUpText;
      case "default-filled":
        return DefaultFilled;
      case "default-free":
        return DefaultFree;
      case "column-filled":
        return ColumnFilled;
      case "column-free":
        return ColumnFree;
      case "media-filled":
        return MediaFilled;
      case "square-middle-price":
        return SquareMiddlePrice;
      default:
        return null;
    }
  }, [previewWidget]);

  if (!items.length) {
    return (
      <Box sx={{ padding: 6, textAlign: "center" }}>
        <Typography variant="h3">Agrega Contenido</Typography>
      </Box>
    );
  }

  return (
    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
      {(items ?? []).map((item, index) => {
        return PreviewWidget ? (
          <Box gridColumn="span 6" key={`${item.id}-${index}`}>
            <PreviewWidget
              title={item.title}
              description={item.previewDescription ? (item?.previewDescription as string) : item.description}
              price={<Price currency="NIO">{item.price != 0 ? getOrderItemPrice(item as unknown as OrderItem) : sanitizeNumber(item?.startingPrice)}</Price>}
              image={getThumbnailImage({ file: item?.images, organizationId: activeOrganization, size: '150x150' })}
              onClick={(e: any) => {
                e.stopPropagation();
                dispatch(openItem(item));
                navigate(`?itemId=${item.id}`);
              }}
            />
          </Box>
        ) : null;
      })}
    </Box>
  );
};

export default GridView;
